import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDayInTz, endOfDayInTz, formatDateInputInTz } from '@/utils/timezone';
import { getComparisonPeriod, type ComparisonPeriod } from '@/utils/periodComparison';
import { useVisibility } from '@/hooks/useVisibility';

// NOTE: metrics are job-based, not lead-based — leads.assigned_tech_id is not
// populated in this dataset (verified 2026-09: 0 of 2734 leads in 90 days).
// Every job is a converted lead, so jobs assigned = opportunities the tech ran
// and completed jobs = sales won. If lead→tech assignment starts being used,
// a lead-based close rate becomes possible here.
export interface TechPerformanceRow {
  techId: string;
  techName: string;
  totalJobs: number;
  completedJobs: number;
  revenue: number;
  avgTicket: number;
  closeRate: number;
  recalls: number;
}

export interface TechScoreboardTotals {
  totalJobs: number;
  completedJobs: number;
  totalRevenue: number;
  avgTicket: number;
  totalSales: number;
  conversionRate: number;
  recalls: number;
}

export interface TechnicianPerformanceData {
  techs: TechPerformanceRow[];
  totals: TechScoreboardTotals;
  previousTotals?: TechScoreboardTotals;
  comparisonPeriod?: ComparisonPeriod;
}

const emptyRow = (techId: string, techName: string): TechPerformanceRow => ({
  techId,
  techName,
  totalJobs: 0,
  completedJobs: 0,
  revenue: 0,
  avgTicket: 0,
  closeRate: 0,
  recalls: 0,
});

const fetchTechPerformance = async (
  from: Date,
  to: Date,
  timezone: string
): Promise<{ techs: TechPerformanceRow[]; totals: TechScoreboardTotals }> => {
  const startISO = startOfDayInTz(from, timezone).toISOString();
  const endISO = endOfDayInTz(to, timezone).toISOString();
  const fromDateStr = formatDateInputInTz(from, timezone);
  const toDateStr = formatDateInputInTz(to, timezone);

  const [scheduledRes, completedRes, techsRes] = await Promise.all([
    supabase
      .from('jobs')
      .select('id, status, assigned_tech_id, is_recall')
      .not('assigned_tech_id', 'is', null)
      .gte('scheduled_date', startISO)
      .lte('scheduled_date', endISO),
    supabase
      .from('jobs')
      .select('id, invoice_total, assigned_tech_id')
      .not('assigned_tech_id', 'is', null)
      .eq('status', 'completed')
      .gte('job_date', fromDateStr)
      .lte('job_date', toDateStr),
    // No is_active filter: inactive techs with historical jobs still need names
    supabase.from('techs').select('id, name'),
  ]);

  if (scheduledRes.error) throw scheduledRes.error;
  if (completedRes.error) throw completedRes.error;
  if (techsRes.error) throw techsRes.error;

  const techNames = new Map<string, string>();
  (techsRes.data || []).forEach((t) => techNames.set(t.id, t.name));

  const rows = new Map<string, TechPerformanceRow>();
  const getRow = (techId: string): TechPerformanceRow => {
    let row = rows.get(techId);
    if (!row) {
      row = emptyRow(techId, techNames.get(techId) || 'Unknown');
      rows.set(techId, row);
    }
    return row;
  };

  (scheduledRes.data || []).forEach((j) => {
    const row = getRow(j.assigned_tech_id as string);
    row.totalJobs++;
    if (j.is_recall) row.recalls++;
  });

  (completedRes.data || []).forEach((j) => {
    const row = getRow(j.assigned_tech_id as string);
    row.completedJobs++;
    row.revenue += j.invoice_total || 0;
  });

  const techs = Array.from(rows.values())
    .map((row) => ({
      ...row,
      avgTicket: row.completedJobs > 0 ? row.revenue / row.completedJobs : 0,
      closeRate: row.totalJobs > 0 ? (row.completedJobs / row.totalJobs) * 100 : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue);

  const totalJobs = techs.reduce((s, t) => s + t.totalJobs, 0);
  const completedJobs = techs.reduce((s, t) => s + t.completedJobs, 0);
  const totalRevenue = techs.reduce((s, t) => s + t.revenue, 0);
  const recalls = techs.reduce((s, t) => s + t.recalls, 0);

  return {
    techs,
    totals: {
      totalJobs,
      completedJobs,
      totalRevenue,
      avgTicket: completedJobs > 0 ? totalRevenue / completedJobs : 0,
      totalSales: completedJobs,
      conversionRate: totalJobs > 0 ? (completedJobs / totalJobs) * 100 : 0,
      recalls,
    },
  };
};

export const useTechnicianPerformance = (
  dateFrom: Date,
  dateTo: Date,
  timezone: string,
  compareEnabled: boolean
) => {
  const { canSeeCompanyFinancials } = useVisibility();
  return useQuery<TechnicianPerformanceData>({
    enabled: canSeeCompanyFinancials,
    queryKey: [
      'technician-performance',
      dateFrom.toISOString(),
      dateTo.toISOString(),
      timezone,
      compareEnabled,
    ],
    queryFn: async () => {
      if (!compareEnabled) {
        return await fetchTechPerformance(dateFrom, dateTo, timezone);
      }
      const comparisonPeriod = getComparisonPeriod(dateFrom, dateTo);
      const [current, previous] = await Promise.all([
        fetchTechPerformance(dateFrom, dateTo, timezone),
        fetchTechPerformance(comparisonPeriod.start, comparisonPeriod.end, timezone),
      ]);
      return { ...current, previousTotals: previous.totals, comparisonPeriod };
    },
    staleTime: 60_000,
  });
};
